import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'searchAppsPlaceholder'
	| 'loadingApps'
	| 'filterByApps'
	| 'clearAll'
	| 'backButtonLabel'
	| 'connect'
	| 'selectApp'
	| 'deselectApp'
	| 'connectApp'
	| 'sourcesHeader'
	| 'appsHeader'
	| 'spaceAppsHeader'
	| 'includeWebResults'
	| 'searchCompanyKnowledge'
	| 'strictSourceMode'
	| 'strictSourceModeTooltip'
	| 'strictSourceModeDescription'
	| 'newBadge'
	| 'viewMore'
	| 'viewMoreModalTitle'
	| 'viewMoreConnectedApps'
	| 'searchSettingsPlaceholder'
	| 'rovoSettingsHeader'
	| 'connectedAppsDescription'
	| 'connectedAppsOutOfTotal'
	| 'connectMoreApps'
	| 'appsAvailableToConnectSectionHeader'
	| 'discoverMoreApps';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		searchAppsPlaceholder: {
			id: 'ai-mate.chat-input.mode.menu.search-apps.placeholder',
			defaultMessage: 'Search apps',
			description: 'The placeholder of the search input',
		},
		loadingApps: {
			id: 'ai-mate.chat-input.mode.menu.loading-apps',
			defaultMessage: 'Loading apps...',
			description: 'The message displayed when apps are loading',
		},
		filterByApps: {
			id: 'ai-mate.chat-input.mode.menu.filter-by-apps',
			defaultMessage: 'Filter by apps',
			description: 'The title of the filter by apps section',
		},
		clearAll: {
			id: 'ai-mate.chat-input.mode.menu.clear-all',
			defaultMessage: 'Clear all',
			description: 'The label of the clear all button',
		},
		backButtonLabel: {
			id: 'ai-mate.chat-input.mode.menu.back-button-label',
			defaultMessage: 'Go back',
			description: 'The label of the clear all button',
		},
		connect: {
			id: 'ai-mate.chat-input.mode.menu.connect-button-label',
			defaultMessage: 'Connect',
			description: 'The label of the connect button for unconnected apps',
		},
		selectApp: {
			id: 'ai-mate.chat-input.mode.menu.select-app',
			defaultMessage: 'Select {appName}',
			description: 'Aria label for selecting an app',
		},
		deselectApp: {
			id: 'ai-mate.chat-input.mode.menu.deselect-app',
			defaultMessage: 'Deselect {appName}',
			description: 'Aria label for deselecting an app',
		},
		connectApp: {
			id: 'ai-mate.chat-input.mode.menu.connect-app',
			defaultMessage: 'Connect {appName}',
			description: 'Aria label for connecting to an app',
		},
		sourcesHeader: {
			id: 'ai-mate.chat-input.mode.menu.sources-header',
			defaultMessage: 'Sources',
			description: 'Header for the sources section in the filter popup',
		},
		appsHeader: {
			id: 'ai-mate.chat-input.mode.menu.apps-header',
			defaultMessage: 'Recently connected apps',
			description: 'Header for the apps section in the filter popup',
		},
		spaceAppsHeader: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.chat-mode-menu.app-filter.spaceAppsHeader',
			defaultMessage: 'Apps',
			description:
				'Header for the single list of connected and available apps in the Rovo Space sources filter',
		},
		includeWebResults: {
			id: 'ai-mate.chat-input.mode.menu.include-web-results',
			defaultMessage: 'Include web results',
			description: 'Label for the web results toggle in the filter popup',
		},
		searchCompanyKnowledge: {
			id: 'ai-mate.chat-input.mode.menu.search-company-knowledge',
			defaultMessage: 'Search company knowledge',
			description: 'Label for the company knowledge toggle in the filter popup',
		},
		strictSourceMode: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.chat-mode-menu.app-filter.strictSourceMode',
			defaultMessage: 'Use specific knowledge',
			description:
				'Label for a toggle in the sources filter that restricts a Rovo prompt to sources added to the current space',
		},
		strictSourceModeTooltip: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.chat-mode-menu.app-filter.strictSourceModeTooltip',
			defaultMessage: 'Rovo will only answer with knowledge you add to this space',
			description:
				'Tooltip explaining what happens when the user enables the specific knowledge toggle in a Rovo Space',
		},
		strictSourceModeDescription: {
			id: 'conversation-assistant-chat-prompt-input.chat-prompt-input.chat-mode-menu.app-filter.strictSourceModeDescription',
			defaultMessage: 'Other sources are unavailable when ‘Use specific knowledge’ is selected',
			description:
				'Helper text shown when strict source mode is enabled, explaining why the other source controls are hidden',
		},
		newBadge: {
			id: 'ai-mate.chat-input.mode.menu.new-badge',
			defaultMessage: 'New',
			description:
				'Badge label shown next to newly enabled apps (enabled by admin within the last 30 days and not yet connected by the user)',
		},
		viewMore: {
			id: 'ai-mate.chat-input.mode.menu.view-more',
			defaultMessage: 'View more',
			description: 'Entry at the bottom of the filter popup list to view more options',
		},
		viewMoreModalTitle: {
			id: 'ai-mate.chat-input.mode.menu.view-more-modal-title',
			defaultMessage: 'Sources & apps',
			description: 'Title of the View More modal dialog showing available sources and apps',
		},
		viewMoreConnectedApps: {
			id: 'ai-mate.chat-input.mode.menu.view-more-connected-apps',
			defaultMessage: 'Connected apps',
			description: 'Side nav item for Connected apps in the View More modal',
		},
		searchSettingsPlaceholder: {
			id: 'ai-mate.chat-input.mode.menu.search-settings-placeholder',
			defaultMessage: 'Search settings',
			description: 'Placeholder for the search box in the View More modal side nav',
		},
		rovoSettingsHeader: {
			id: 'ai-mate.chat-input.mode.menu.rovo-settings-header',
			defaultMessage: 'Rovo Settings',
			description: 'Common header for the content section in the View More modal',
		},
		connectedAppsDescription: {
			id: 'ai-mate.chat-input.mode.menu.connected-apps-description',
			defaultMessage: 'Manage web results and company knowledge sources.',
			description: 'Description for the Connected apps section in the View More modal',
		},
		connectedAppsOutOfTotal: {
			id: 'ai-mate.chat-input.mode.menu.connected-apps-out-of-total',
			defaultMessage: '{connectedCount} of your {totalCount} apps are connected',
			description:
				'Summary in the Connected apps modal: how many sources are connected versus the total shown',
		},
		connectMoreApps: {
			id: 'ai-mate.chat-input.mode.menu.connect-more-apps',
			defaultMessage: 'Connect more',
			description:
				'Button next to the connected summary; scrolls to the list of apps available to connect',
		},
		appsAvailableToConnectSectionHeader: {
			id: 'ai-mate.chat-input.mode.menu.apps-available-to-connect-section-header',
			defaultMessage:
				'{count, plural, one {# app available to connect} other {# apps available to connect}}',
			description:
				'Section header in the Connected apps modal showing the number of apps the user can connect',
		},
		discoverMoreApps: {
			id: 'ai-mate.chat-input.mode.menu.discover-more-apps',
			defaultMessage: 'Discover more apps',
			description:
				'Entry at the bottom of the filter popup to open the Rovo Settings modal for discovering and connecting more apps',
		},
	});

export { messages };
